import { format } from 'date-fns';
import { app, auth } from '~/firebase/init';

import { PLATFORM_ID } from '~/globalVariables';
import { UniqItem } from '~/types/store/item';

type RestValue = Record<string, any>;

// Firestore REST 回傳的型別化值轉回一般 JS 值（double 的 Infinity / NaN 以字串表示）
function decodeValue(value: RestValue): any {
  if ('mapValue' in value) {
    return Object.fromEntries(
      Object.entries(value.mapValue.fields || {}).map(([key, v]) => [key, decodeValue(v as RestValue)]),
    );
  }
  if ('arrayValue' in value) return (value.arrayValue.values || []).map(decodeValue);
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('nullValue' in value) return null;
  return Object.values(value)[0];
}

// field path 的每一段都加上反引號，避免 20220101 這類數字 key 被視為非法識別字
const fieldPath = (...segments: Array<string>) => segments.map((s) => `\`${s}\``).join('.');

/**
 * 依平台訂購數排序取得所有商品，供熱門商品列表使用。
 *
 * 改走 REST runQuery 而非 SDK 的 getDocs：
 * 1. 不佔用 SDK 共用的 Listen channel，避免大量商品資料卡住其他 listener
 * 2. 用 select 只取列表需要的欄位，inventory 只取取餐當天，略過累積的歷史日期
 *
 * 回傳的商品只包含 select 的欄位（modifiers、containers、tax 等不存在），
 * 進入商品頁時會另外讀取完整資料。
 */
export default async function getOrderCountInPlatform(
  pickMealAt: Date | null,
): Promise<Array<UniqItem>> {
  const { projectId, apiKey } = app.options;
  const dateString = pickMealAt ? format(pickMealAt, 'yyyyMMdd') : '';

  const fields = [
    fieldPath('name'),
    fieldPath('description'),
    fieldPath('image'),
    fieldPath('price'),
    fieldPath('tags'),
    fieldPath('isAvailable'),
    fieldPath('orderCountInPlatform', PLATFORM_ID),
    fieldPath('inventory', 'default'),
    ...(dateString
      ? [fieldPath('inventory', 'current', dateString), fieldPath('inventory', 'reserved', dateString)]
      : []),
  ];

  const idToken = await auth.currentUser?.getIdToken();

  const res = await fetch(
    `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents:runQuery?key=${apiKey}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        structuredQuery: {
          from: [{ collectionId: 'items', allDescendants: true }],
          orderBy: [{ field: { fieldPath: fieldPath('orderCountInPlatform', PLATFORM_ID) }, direction: 'DESCENDING' }],
          select: { fields: fields.map((path) => ({ fieldPath: path })) },
        },
      }),
    },
  );

  if (!res.ok) {
    throw new Error(`run popular items query failed: ${res.status}`);
  }

  const results: Array<{ document?: { name: string; fields?: Record<string, RestValue> } }> =
    await res.json();

  return results
    .filter(({ document }) => document)
    .map(({ document }) => {
      // name: projects/{p}/databases/(default)/documents/stores/{storeId}/items/{itemId}
      const segments = document!.name.split('/');
      const id = segments[segments.length - 1];
      const storeId = segments[segments.length - 3];
      const data = decodeValue({ mapValue: { fields: document!.fields || {} } });

      return {
        ...data,
        id,
        uniqId: `${storeId}${id}`,
        storeId,
        inventory: {
          current: {},
          reserved: {},
          ...data.inventory,
        },
      } as UniqItem;
    });
}
